/**
 * QUIPWA — useLoadStarter: the «Cargar ejemplo» button
 *
 * Loads the starter deck and note (lib/starter/starter-pack.ts) in the active
 * language. Only a click calls `mutate`: nothing here runs by itself. The
 * module and its content are fetched on the first click, so they never weigh
 * on the first screen.
 *
 * ```tsx
 * const loadStarter = useLoadStarter()
 * <button onClick={() => loadStarter.mutate()} disabled={loadStarter.isPending}>…</button>
 * ```
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useDB } from './useDB.tsx'
import { DECK_KEYS } from './useDecks.ts'
import { getCollection } from '../lib/db/collection-db-service.ts'
import { useI18n } from '../lib/i18n/i18n-store.ts'
import { useNotesStore } from '../stores/notes-store.ts'
import { toast } from '../components/system/toast-store.ts'
import type { StarterResult } from '../lib/starter/starter-pack.ts'

class StarterUnavailable extends Error {}

export function useLoadStarter() {
  const { services } = useDB()
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (): Promise<StarterResult> => {
      // The Design Lab shows these screens over the real deck database (its
      // notes are in memory). Loading there would write real data that the
      // next sign-in uploads, the way its old demo seed once did.
      if (!services || useNotesStore.getState().mockMode) throw new StarterUnavailable()
      const { loadStarter } = await import('../lib/starter/starter-pack.ts')
      const result = await loadStarter(
        { state: services.state, importer: services.importer, getCollection, uploads: services.deckUploads, notes: services.notes },
        locale,
      )
      // Persist now, as an import does: a reload right after must keep it.
      await Promise.all([services.state.flushNow(), services.localAdapter.flushNow()])
      return result
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: DECK_KEYS.all })
      if (result.createdDeck || result.createdNote) toast.success(t('starter.loaded', { count: result.cardCount }))
      else toast.info(t('starter.alreadyLoaded'))
    },
    onError: (err) => {
      if (err instanceof StarterUnavailable) {
        toast.error(t('starter.notReady'))
        return
      }
      console.error('[Starter] Could not load the example:', err)
      toast.error(t('starter.failed'))
    },
  })
}
